import { DestroyRef, inject, Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	firstValueFrom,
	interval,
	map,
	Subject,
	Subscription,
	switchMap,
	take,
	takeWhile,
	tap,
} from 'rxjs';
import PaySession from '../../models/PaySession';
import { PaySessionSocketService } from '../pay-session-socket/pay-session-socket.service';
import { AuthenticationService } from '../authentication/authentication.service';
import Order from '../../models/Order';
import Venue from '../../models/Venue';
import {
	deepClone,
	getAbsolutValueFromTipSelection,
	getPaySessionStatistics,
	numberD,
	paymentFromOrder,
	sanitizeId,
} from '../../utils/utils';
import Payment from '../../models/Payment';
import { HttpErrorResponse } from '@angular/common/http';
import { PaySessionSplitType } from '../../enums/PaySessionSplitType';
import { SubmitActions, SubmitData } from '@adyen/adyen-web';
import { PaymentMethod } from '../../enums/PaymentMethod';
import { PaymentStatus } from '../../models/PaymentStatus';
import Table from '../../models/Table';
import { CompressedStorageService } from '../compression/compressed-storage.service';
import { AdyenPaymentMethodResponse } from '../../models/AdyenPaymentMethodResponse';
import { TipSelectorValue, TipType } from '../../../app/components/tip-selector/tip-selector.component';
import { ChecksResponse } from '../../models/ChecksResponse';
import PiggyGiftCard from '../../models/PiggyGiftCard';
import { SmoothrCNCDiscount } from '../../models/CNCDiscount';
import { SimphonyDiscountingTableResult } from '../../models/SimphonyDiscountingTableResult';
import { environment } from '../../../environments/environment';
import { OrderStatus } from '../../enums/OrderStatus';
import { AlertController } from '@ionic/angular';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const KEY_PAID_ORDER = 'paidOrder';
const KEY_FINISHED_PAYMENT = 'finishedPayment';

@Injectable({
	providedIn: 'root',
})
export class RepositoryService {
	availableDiscounts$ = new BehaviorSubject<SmoothrCNCDiscount[]>([]);
	private authenticationService = inject(AuthenticationService);
	paySessionObservable = this.authenticationService.paySessionObservable;
	private _generatedOrderSubject = new BehaviorSubject<Order | null>(null);
	// Payment mutex. Generating an order invalidates the previously generated one, so the whole
	// "generate order -> select tip -> enter card data -> confirm" sequence must run as one atomic
	// critical section. A double-press (UI lag / bad connectivity) or any other re-entry while a
	// payment is ongoing must be rejected, never run concurrently. `_paymentInProgress` stays true
	// across the modals (unlike the per-page `loading` spinner flag, which is cleared while modals
	// are open), so it is the authoritative guard both for re-entry and for disabling the buttons.
	private _paymentInProgress = new BehaviorSubject<boolean>(false);
	paymentInProgressObservable = this._paymentInProgress.asObservable();
	private _checksAvailable = new BehaviorSubject(true);
	checksAvailable = this._checksAvailable.asObservable();
	private _selectedTips = new BehaviorSubject<TipSelectorValue>({
		value: 0,
		type: TipType.RELATIVE,
		icon: '',
	});
	paySessionStatisticsObservable = combineLatest([this.paySessionObservable, this._generatedOrderSubject, this._selectedTips]).pipe(
		map(([ps, order, tips]) => (ps ? getPaySessionStatistics(ps, order, tips) : null))
	);
	private apiService = inject(ApiService);
	private paySessionSocket = inject(PaySessionSocketService);
	onReconnect = this.paySessionSocket.onReconnect;
	onReconnecting = this.paySessionSocket.onReconnecting;
	private storageService = inject(CompressedStorageService);
	private alertController = inject(AlertController);
	private destroyRef = inject(DestroyRef);
	private router = inject(Router);
	private _venueSubject = new BehaviorSubject<Venue | null>(null);
	venueObservable = this._venueSubject.asObservable();
	private _table: Table | null = null;
	readonly table = this._table;
	private paySessionUpdateSubscription: Subscription | null = null;
	private _addedGiftCards = new BehaviorSubject<PiggyGiftCard[]>([]);
	addedGiftCards = this._addedGiftCards.asObservable();
	private _discountingResultSubject = new Subject<SimphonyDiscountingTableResult[]>();
	discountingResultObservable = this._discountingResultSubject.asObservable();
	private currentSocketTimeout = 180_000;
	private checkAvailableSimphonyChecksInterval = interval(30_000);

	constructor() {
		this.paySessionObservable.pipe(distinctUntilChanged((prev, curr) => prev?._id !== curr?._id)).subscribe(_ => {
			this.paySessionUpdateSubscription?.unsubscribe();
			this.paySessionUpdateSubscription = this.paySessionSocket.paySessionUpdate().subscribe(async update => {
				this.authenticationService.onPaySessionUpdate(update);
			});
		});
		this.onReconnect.subscribe(async () => {
			try {
				await this.handleReconnect();
			} catch (e) {
				console.error('Error while handling reconnect', e);
			}
		});
		this.updateSessionIfPresent();
		this.checkAvailableSimphonyChecksInterval.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
			await this.checkAvailableSimphonyChecks();
		});
	}

	get paySession(): PaySession | null {
		return this.authenticationService.paySession;
	}

	get generatedOrder(): Order | null {
		return this._generatedOrderSubject.value;
	}

	get isPaymentInProgress(): boolean {
		return this._paymentInProgress.value;
	}

	/**
	 * Runs `operation` (a full generate-order + pay sequence) as an exclusive critical section.
	 * If a payment is already in progress the call is rejected and resolves to `null` instead of
	 * running concurrently — this is what stops a double-press from generating a second order and
	 * invalidating the one the user is currently paying. The lock is always released in `finally`,
	 * so success, failure and user-cancelled modals all free it.
	 */
	async runExclusivePayment<T>(operation: () => Promise<T>): Promise<T | null> {
		if (this._paymentInProgress.value) {
			console.warn('RepositoryService: payment already in progress — ignoring duplicate trigger');
			return null;
		}
		this._paymentInProgress.next(true);
		try {
			return await operation();
		} finally {
			this._paymentInProgress.next(false);
		}
	}

	private _venue: Venue | null = null;

	get venue(): Venue | null {
		return this._venue;
	}

	private _paidOrder: Order | null = this.storageService.getItem(KEY_PAID_ORDER);

	private _currencyFormat = new Intl.NumberFormat('de-DE', {
		style: 'currency',
		currency: this._paidOrder?.currency ?? this._venue?.currency ?? 'EUR',
	});
	readonly currencyFormat = this._currencyFormat;

	get paidOrder(): Order | null {
		return this._paidOrder;
	}

	set paidOrder(order: Order | null) {
		this._paidOrder = order;
		this.storageService.setItem(KEY_PAID_ORDER, order);
	}

	private _finishedPayment: Payment | null = this.storageService.getItem(KEY_FINISHED_PAYMENT);

	get finishedPayment(): Payment | null {
		return this._finishedPayment;
	}

	private set finishedPayment(payment: Payment | null) {
		this._finishedPayment = payment;
		this.storageService.setItem(KEY_FINISHED_PAYMENT, payment);
	}

	private _createdPaymentsValue: Payment[] = [];

	get createdPaymentsValue(): Payment[] {
		return this._createdPaymentsValue;
	}

	private set createdPaymentsValue(payments: Payment[]) {
		this._createdPaymentsValue = payments;
	}

	async updateSessionIfPresent() {
		console.log('updateSessionIfPresent()');
		if (!this.authenticationService.paySession) {
			console.log('No Session in storage.');
			return;
		}
		try {
			const previousSession = this.authenticationService.paySession;
			const paySession = await firstValueFrom(this.apiService.resume());
			// The resume response does not carry the client-side readableId/tableNumber
			// (participate() sets them manually), so keep them from the stored session.
			paySession.readableId = paySession.readableId ?? previousSession?.readableId;
			paySession.tableNumber = paySession.tableNumber ?? previousSession?.tableNumber;
			this._generatedOrderSubject.next(null);
			this.authenticationService.onPaySessionChange(paySession);
		} catch (e) {
			console.error(e);
			if (e instanceof HttpErrorResponse)
				switch (e.status) {
					case 401:
						console.log('Not authorized');
						this.authenticationService.onUnauthorized();
						break;
					case 404:
						console.log('No session found!');
						this.authenticationService.onUnauthorized();
						break;
				}
			throw e;
		}
	}

	async getVenue(readableId: string): Promise<Venue> {
		if (this._venue?.readableId === sanitizeId(readableId)) {
			return this._venue;
		}
		return await firstValueFrom(
			this.apiService.getLazyVenue(sanitizeId(readableId)).pipe(
				tap(venue => {
					this._currencyFormat = new Intl.NumberFormat('de-DE', { currency: venue.currency });
					this._venue = venue;
					this._venueSubject.next(venue);
					this.loadAvailableDiscounts(venue._id);
				})
			)
		);
	}

	async getTable(venue: string, tableNumber: string): Promise<Table> {
		if (this.table?.venue === venue && this.table.number === tableNumber) {
			return this.table;
		}
		return await firstValueFrom(this.apiService.getTable(venue, tableNumber));
	}

	async getPaySessionByVenueAndTable(readableId: string, tableNumber: string): Promise<PaySession | null> {
		return firstValueFrom(this.apiService.getPaySessionByVenueAndTable(sanitizeId(readableId), tableNumber));
	}

	async leaveSession(): Promise<void> {
		await firstValueFrom(this.apiService.leave());
		this.authenticationService.onPaySessionLeaved();
	}

	async getGiftCard(code: string, venue?: string): Promise<PiggyGiftCard> {
		return await firstValueFrom(this.apiService.getGiftCardByCode(code, venue ?? this._venue?._id ?? ''));
	}

	/**
	 *
	 * @param venue
	 * @param table
	 *
	 * @throws HttpErrorResponse if not authorized
	 * @throws NoCheckAvailableError
	 */
	async participate(venue: string, table: string): Promise<PaySession> {
		const sanitizedVenue = sanitizeId(venue);
		this._selectedTips.next({
			type: TipType.RELATIVE,
			value: 0,
			icon: '',
		});
		if (
			this.authenticationService.paySession &&
			(this.authenticationService.paySession.readableId === sanitizedVenue || !venue) &&
			(this.authenticationService.paySession.tableNumber === table || !table)
		) {
			await this.paySessionSocket.connect();
			return this.authenticationService.paySession;
		}
		try {
			const paySession = await firstValueFrom(
				this.apiService.participate({
					readableId: sanitizedVenue,
					tableNumber: table,
				})
			);

			paySession.readableId = sanitizedVenue;
			paySession.tableNumber = table;
			this.authenticationService.onPaySessionChange(paySession);
			await this.paySessionSocket.connect();
			return paySession;
		} catch (e) {
			if (e instanceof HttpErrorResponse) {
				switch (e.status) {
					case 404:
						if (e.error.name === 'NoCheckAvailableError') {
							throw Error(e.error.name);
						}
				}
			}
			throw e;
		}
	}

	async getChecks(): Promise<Order[]> {
		return await firstValueFrom(this.apiService.getChecks());
	}

	async selectSplit(splitType: PaySessionSplitType | null, guestCount: number): Promise<PaySession> {
		const paySession = await firstValueFrom(this.apiService.selectSplit(splitType, guestCount));
		this.authenticationService.onPaySessionUpdate(paySession);
		return paySession;
	}

	async discounting(promoCodes: string[], contactUuid?: string): Promise<SimphonyDiscountingTableResult[]> {
		const results = await firstValueFrom(this.apiService.discounting(promoCodes, contactUuid));
		this._discountingResultSubject.next(results);
		return results;
	}

	async reserve(ids: string[]): Promise<Order[]> {
		try {
			return await firstValueFrom(this.apiService.reserve(ids));
		} catch (e) {
			console.error(e);
			if (e instanceof HttpErrorResponse && e.status === 401) {
				console.log('Not authorized');
				this.authenticationService.onUnauthorized();
			}
			throw e;
		}
	}

	async generateOrder(): Promise<Order> {
		const generatedOrder = await firstValueFrom(this.apiService.generateOrderForParticipant());
		this._generatedOrderSubject.next(generatedOrder);
		this._selectedTips.next({
			value: 0,
			type: TipType.RELATIVE,
			icon: '',
		});
		return generatedOrder;
	}

	async generateOrderAll(): Promise<Order> {
		const generatedOrder = await firstValueFrom(this.apiService.generateOrderAllForParticipant());
		this._generatedOrderSubject.next(generatedOrder);
		return generatedOrder;
	}

	async getChecksOfTable(venue: string, table: string): Promise<ChecksResponse> {
		return await firstValueFrom(this.apiService.getChecksOfTable(sanitizeId(venue), table));
	}

	async getOrder(id: string): Promise<Order> {
		return firstValueFrom(this.apiService.getOrder(id ?? ''));
	}

	async getPayment(id: string): Promise<Payment> {
		return firstValueFrom(this.apiService.getPayment(id ?? ''));
	}

	/**
	 * Polls payment status until it changes from 'reserved' to either 'done' or 'failed'
	 * @param paymentId - The payment ID to poll
	 * @param intervalMs - Polling interval in milliseconds (default: 2000ms)
	 * @param maxAttempts - Maximum number of polling attempts (default: 150 = 5 minutes)
	 * @returns Observable that emits payment status updates
	 */
	pollPaymentStatus(paymentId: string, intervalMs: number = 2000, maxAttempts: number = 150) {
		return interval(intervalMs).pipe(
			switchMap(() => this.apiService.getPayment(paymentId)),
			takeWhile(payment => {
				return (
					payment.status === PaymentStatus.created ||
					payment.status === PaymentStatus.reserved ||
					payment.status === PaymentStatus.pending
				);
			}, true),
			take(maxAttempts)
		);
	}

	/**
	 * Polls order status until `isPayed` becomes true or the order fails.
	 * This handles multi-payment scenarios (e.g. gift card + credit card)
	 * since the order is only marked as paid when ALL payments are done.
	 * @param orderId - The order ID to poll
	 * @param intervalMs - Polling interval in milliseconds (default: 2000ms)
	 * @param maxAttempts - Maximum number of polling attempts (default: 150 = 5 minutes)
	 * @returns Observable that emits order updates
	 */
	pollOrderStatus(orderId: string, intervalMs: number = 2000, maxAttempts: number = 150) {
		return interval(intervalMs).pipe(
			switchMap(() => this.apiService.getOrder(orderId)),
			takeWhile(order => {
				// Keep polling while not paid and not failed
				return !order.isPayed && order.status !== OrderStatus.FAILED;
			}, true),
			take(maxAttempts)
		);
	}

	async createPayment(payment: Partial<Payment>, giftCards: PiggyGiftCard[] = []): Promise<Payment> {
		return firstValueFrom(this.apiService.createPayment(payment)); //
		// const giftCardPayment = this.generateGiftCardPayment(giftCards, payment);
		// payment.sum = giftCardPayment.remainingValue;
		// console.log('adyenPayment', payment);
		// return (await this.createPayments([payment, giftCardPayment.payment].filter(it => (it.sum ?? 0) > 0)))[0];
	}

	async createPayments(payments: Partial<Payment>[]): Promise<Payment[]> {
		console.log('createPayments()', payments);
		return firstValueFrom(this.apiService.createPaymentMultiple(payments));
	}

	async finishPaypalPayment(paypalOrderId: string): Promise<Payment> {
		return firstValueFrom(this.apiService.finishPaypalPayment(paypalOrderId));
	}

	async applePaySession(
		venueId: string,
		body: {
			url: string;
			venue: string;
			channel: string;
			customerGroup: string;
		}
	): Promise<any> {
		return firstValueFrom(this.apiService.applePaySession(venueId, body));
	}

	async finishAdyenPayment(
		order: Order,
		submitData: SubmitData,
		submitActions: SubmitActions,
		tip: TipSelectorValue,
		giftCards: PiggyGiftCard[] = []
	): Promise<Payment> {
		console.log('DISMISSING WITH DATA');
		try {
			const generatedPayment = paymentFromOrder(order);
			const tipAbsoluteValue = getAbsolutValueFromTipSelection(generatedPayment.sum ?? 0, tip);

			// Build the list of payments to create
			const payments: Partial<Payment>[] = [];

			let creditCardSum = numberD(generatedPayment.sum);

			// If gift cards are applied, create a gift card payment first
			if (giftCards.length > 0) {
				const giftCardResult = this.generateGiftCardPayment(giftCards, generatedPayment);
				if (numberD(giftCardResult.payment.sum) > 0) {
					payments.push(giftCardResult.payment);
				}
				creditCardSum = giftCardResult.remainingValue;
			}

			// Add credit card payment for remaining amount
			if (creditCardSum > 0) {
				payments.push({
					...generatedPayment,
					sum: creditCardSum,
					tip: tipAbsoluteValue,
					method: PaymentMethod.CREDIT_CARD,
					adyenPayment: {
						card: {
							number: (submitData.data.paymentMethod as any).encryptedCardNumber ?? '',
							month: (submitData.data.paymentMethod as any).encryptedExpiryMonth ?? '',
							year: (submitData.data.paymentMethod as any).encryptedExpiryYear ?? '',
							code: (submitData.data.paymentMethod as any).encryptedSecurityCode ?? '',
						},
					},
					preAuth: true,
				});
			}

			const createdPayments = await this.createPayments(payments);
			this.createdPaymentsValue = createdPayments;
			const preparedPayment = createdPayments[createdPayments.length - 1];

			const paidOrder = await this.getOrder(order._id);
			paidOrder.venueReadableId = order.venueReadableId;
			console.log(preparedPayment);
			if (preparedPayment.status === PaymentStatus.created && preparedPayment.adyenPayment?.redirectUrl) {
				const form = document.createElement('form');
				form.method = 'POST';
				form.action = preparedPayment.adyenPayment.redirectUrl;
				const paReqInput = document.createElement('input');
				paReqInput.type = 'hidden';
				paReqInput.name = 'PaReq';
				paReqInput.value = preparedPayment.adyenPayment.cardAuthRequestPaReq ?? '';
				form.appendChild(paReqInput);
				const mdInput = document.createElement('input');
				mdInput.type = 'hidden';
				mdInput.name = 'MD';
				mdInput.value = preparedPayment.adyenPayment.cardPaymentSessionMd ?? '';
				form.appendChild(mdInput);
				document.body.appendChild(form);
				form.submit();
			} else if (preparedPayment.status === PaymentStatus.done) {
				submitActions.resolve({
					resultCode: 'Authorised',
				});
				this.finishedPayment = preparedPayment;
				this.paidOrder = paidOrder;
				this.clearGiftCards();
			}
			return preparedPayment;
		} catch (error) {
			console.log('REJECTING with ERROR', error);
			submitActions.reject();
			throw error;
		}
	}

	async finishGooglePayAdyenPayment(
		order: Order,
		token: string,
		submitActions: SubmitActions,
		tip: TipSelectorValue,
		giftCards: PiggyGiftCard[] = []
	): Promise<Payment> {
		console.log('finishGooglePayAdyenPayment', token);
		try {
			const generatedPayment = paymentFromOrder(order);
			const tipAbsoluteValue = getAbsolutValueFromTipSelection(generatedPayment.sum ?? 0, tip);

			const payments: Partial<Payment>[] = [];
			let gpaySum = numberD(generatedPayment.sum);

			if (giftCards.length > 0) {
				const giftCardResult = this.generateGiftCardPayment(giftCards, generatedPayment);
				if (numberD(giftCardResult.payment.sum) > 0) {
					payments.push(giftCardResult.payment);
				}
				gpaySum = giftCardResult.remainingValue;
			}

			if (gpaySum > 0) {
				payments.push({
					...generatedPayment,
					sum: gpaySum,
					tip: tipAbsoluteValue,
					method: PaymentMethod.GPAY,
					adyenPayment: {
						gpToken: token,
					},
					preAuth: true,
				});
			}

			const createdPayments = await this.createPayments(payments);
			this.createdPaymentsValue = createdPayments;
			const preparedPayment = createdPayments[createdPayments.length - 1];

			const paidOrder = await this.getOrder(order._id);
			paidOrder.venueReadableId = order.venueReadableId;
			console.log(preparedPayment);
			if (preparedPayment.status === PaymentStatus.created && preparedPayment.adyenPayment?.redirectUrl) {
				const form = document.createElement('form');
				form.method = 'POST';
				form.action = preparedPayment.adyenPayment.redirectUrl;
				const paReqInput = document.createElement('input');
				paReqInput.type = 'hidden';
				paReqInput.name = 'PaReq';
				paReqInput.value = preparedPayment.adyenPayment.cardAuthRequestPaReq ?? '';
				form.appendChild(paReqInput);
				const mdInput = document.createElement('input');
				mdInput.type = 'hidden';
				mdInput.name = 'MD';
				mdInput.value = preparedPayment.adyenPayment.cardPaymentSessionMd ?? '';
				form.appendChild(mdInput);
				document.body.appendChild(form);
				form.submit();
			} else if (preparedPayment.status === PaymentStatus.done || preparedPayment.status === PaymentStatus.reserved) {
				submitActions.resolve({
					resultCode: 'Authorised',
				});
				this.finishedPayment = preparedPayment;
				this.paidOrder = paidOrder;
				this.paidOrder.venueReadableId = order.venueReadableId;
				this.clearGiftCards();
			}
			return preparedPayment;
		} catch (error) {
			console.log('REJECTING with ERROR', error);
			submitActions.reject();
			throw error;
		}
	}

	async finishApplePayment(
		order: Order,
		tip: number,
		session: ApplePaySession,
		event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
		giftCards: PiggyGiftCard[] = []
	): Promise<Payment> {
		try {
			const basePayment = paymentFromOrder(order);
			console.log({
				basePayment,
			});

			const payments: Partial<Payment>[] = [];
			let applePaySum = numberD(basePayment.sum);

			if (giftCards.length > 0) {
				const giftCardResult = this.generateGiftCardPayment(giftCards, basePayment);
				if (numberD(giftCardResult.payment.sum) > 0) {
					payments.push(giftCardResult.payment);
				}
				applePaySum = giftCardResult.remainingValue;
			}

			if (applePaySum > 0) {
				payments.push({
					...basePayment,
					sum: applePaySum,
					method: PaymentMethod.APAY,
					adyenPayment: {
						apToken: JSON.stringify(event.payment.token.paymentData),
					},
					tip,
					preAuth: true,
				});
			}

			console.log(session);
			const createdPayments = await this.createPayments(payments);
			this.createdPaymentsValue = createdPayments;
			const finishedPayment = createdPayments[createdPayments.length - 1];
			if (finishedPayment.status !== PaymentStatus.done && finishedPayment.status !== PaymentStatus.reserved) {
				throw Error(`Payment status is ${finishedPayment.status} instead of ${PaymentStatus.done}.`);
			}
			session.completePayment({
				status: ApplePaySession.STATUS_SUCCESS,
			});
			this.paidOrder = await this.getOrder(order._id);
			this.paidOrder.venueReadableId = order.venueReadableId;
			console.log('paidOrder', this.paidOrder);
			this.finishedPayment = finishedPayment;
			this.clearGiftCards();
			return finishedPayment;
		} catch (e) {
			session.completePayment(ApplePaySession.STATUS_FAILURE);
			console.error(e);
			throw e;
		}
	}

	async validateApplePayment(order: Order, session: ApplePaySession, event: ApplePayJS.ApplePayValidateMerchantEvent) {
		try {
			const result = await this.applePaySession(order.venue, {
				url: event.validationURL,
				venue: order.venue,
				channel: 'web',
				customerGroup: environment.customerGroup,
			});

			console.log('applePaySessionResult', result);
			session.completeMerchantValidation(result);
		} catch (e) {
			console.log(e);
			session.completePayment(ApplePaySession.STATUS_FAILURE);
			throw e;
		}
	}

	generateGiftCardPayment(
		giftCards: PiggyGiftCard[],
		basePayment: Partial<Payment>
	): {
		payment: Partial<Payment>;
		remainingValue: number;
	} {
		const generatedPayment = deepClone(basePayment);
		const totalPrice = numberD(generatedPayment.sum);

		generatedPayment.method = PaymentMethod.PIGGY_GIFT_CARD;
		generatedPayment.piggyGiftCards = giftCards.map(it => it.hash);
		const totalPiggyBalance = giftCards.reduce((acc, it) => acc + it.balance, 0);
		if (totalPiggyBalance < totalPrice) {
			generatedPayment.sum = totalPiggyBalance;
		}
		return {
			payment: generatedPayment,
			remainingValue: Math.abs(totalPrice - totalPiggyBalance),
		};
	}

	async reloadFinishedPayment() {
		const paidOrder = this.paidOrder;
		if (paidOrder?.payment) {
			try {
				this.finishedPayment = await this.getPayment(paidOrder.payment);
			} catch (e) {
				console.error('Error while loading payment of', paidOrder);
			}
		}
	}

	async finishGiftCardPayment(order: Order, giftCards: PiggyGiftCard[], tip: TipSelectorValue): Promise<Payment> {
		console.log(`finishGiftCardPayment(order: ${order._id}, giftCards: ${giftCards.length}, tip: ${JSON.stringify(tip)})`);
		try {
			const generatedPayment = paymentFromOrder(order);
			const tipAbsoluteValue = getAbsolutValueFromTipSelection(generatedPayment.sum ?? 0, tip);
			const payment = await this.createPayments([
				{
					...generatedPayment,
					tip: tipAbsoluteValue,
					method: PaymentMethod.PIGGY_GIFT_CARD,
					piggyGiftCards: giftCards.map(c => c.hash),
				},
			]);
			const paidOrder = await this.getOrder(order._id);
			paidOrder.venueReadableId = order.venueReadableId;

			this.paidOrder = paidOrder;
			this.clearGiftCards();
			return payment[0];
		} catch (error) {
			console.log('REJECTING with ERROR', error);
			throw error;
		}
	}

	async sendPaymentEmail(payment: string, email: string): Promise<void> {
		return firstValueFrom(this.apiService.sendPaymentEmail(payment, email));
	}

	clear() {
		this.paidOrder = null;
		this.finishedPayment = null;
	}

	clearGeneratedOrder() {
		this._generatedOrderSubject.next(null);
	}

	getAdyenPaymentMethods(venue: string): Promise<AdyenPaymentMethodResponse> {
		return firstValueFrom(this.apiService.getAdyenMethods(venue));
	}

	async killCheck(readableId: string, tableNumber: string) {
		console.log('kill check for ', readableId, tableNumber);
		const result = await firstValueFrom(this.apiService.killCheck(readableId, tableNumber));
		console.log('result: ', result);
	}

	async setSocketTimeout(timeoutInMs: number) {
		this.currentSocketTimeout = timeoutInMs;
		try {
			await firstValueFrom(this.apiService.setSocketTimeout(timeoutInMs));
		} catch (e) {
			throw new Error('Error while setting socketTimeout');
		}
	}

	async handleReconnect() {
		const newPaySession = await firstValueFrom(this.apiService.resume());
		this.authenticationService.onPaySessionChange(newPaySession);
		console.log('Updated paySession after reconnect:', newPaySession);
		const prevGeneratedOrder = this._generatedOrderSubject.value;
		if (!prevGeneratedOrder) {
			console.log('No order generated yet.');
			return;
		}
		try {
			await this.setSocketTimeout(this.currentSocketTimeout);
			console.log(`Socket Timeout set to ${this.currentSocketTimeout}ms after reconnect`);
		} catch (e) {
			console.error('Error while setting timeout after reconnect', e);
		}
		try {
			const currentOrder = await this.getOrder(prevGeneratedOrder._id);
			console.log('Loaded order after reconnect.', currentOrder.code, currentOrder.status, 'prev', prevGeneratedOrder.status);
			if (currentOrder.status === OrderStatus.FAILED) {
				Sentry.captureMessage(`Order failed after reconnect ${currentOrder.code}`);
				const alert = await this.alertController.create({
					header: 'Sitzung abgelaufen.',
					message: 'Deine Sitzung ist abgelaufen. Bitte lade die Seite neu um fortzufahren.',
					buttons: [
						{
							text: 'OK',
							handler: () => {
								window.location.reload();
							},
						},
					],
					backdropDismiss: false,
				});
				await alert.present();
			} else if (currentOrder.isPayed) {
				Sentry.captureMessage(`Order PAID after reconnect ${currentOrder.code}, redirect to payment success!`);
				this.paidOrder = currentOrder;
				try {
					this._finishedPayment = await firstValueFrom(this.apiService.getPayment(currentOrder.payment));
				} catch (e) {
					console.error(e);
				}
				await this.router.navigate(['/payment/success']);
			}
			this._generatedOrderSubject.next(currentOrder);
		} catch (e) {
			console.log('Error while loading order after reconnect.');
			console.error(e);
			window.location.reload();
		}
	}

	async checkAvailableSimphonyChecks() {
		// const checkReferences = this.paySession?.checks.map(it => it.externalOrderId!!.split('_')[0]) ?? [];
		// if (checkReferences.length === 0) {
		// 	this._checksAvailable.next(true);
		// 	return;
		// }
		// try {
		// 	const checksAvailable = await firstValueFrom(
		// 		this.apiService.checkChecksAvailable(this.paySession?.venue!!, this.paySession?.tableNumber!!, checkReferences)
		// 	);
		// 	this._checksAvailable.next(checksAvailable);
		// } catch (e) {
		// 	console.error(`Error while checking if checks ${checkReferences} are available`, e);
		// 	this._checksAvailable.next(false);
		// }
	}

	addGiftCard(giftCard: PiggyGiftCard) {
		this._addedGiftCards.next([...this._addedGiftCards.value.filter(card => card.hash !== giftCard.hash), giftCard]);
	}

	removeGiftCard(hash: string) {
		this._addedGiftCards.next([...this._addedGiftCards.value.filter(card => card.hash !== hash)]);
	}

	clearGiftCards() {
		this._addedGiftCards.next([]);
	}

	onTipsSelected(selectedTips: TipSelectorValue) {
		this._selectedTips.next(selectedTips);
	}

	private async loadAvailableDiscounts(venue: string) {
		try {
			this.availableDiscounts$.next(await firstValueFrom(this.apiService.getCNCDiscountsToday(venue)));
		} catch (e) {
			console.error('Error while loading available discounts', e);
			this.availableDiscounts$.next([]);
		}
	}
}
